// project import
import React, { Suspense, useState, useEffect, createContext, useContext } from 'react';
import { HashRouter } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import Routes from '@/routes';
import ScrollTop from '@/components/ScrollTop';
import { getAntdTheme } from '@/themes/antd-theme';

import { ChatProvider } from '@/components/FloatingChatPackage/contexts/ChatContext';
import FloatingChat from '@/components/FloatingChatPackage/components/FloatingChat';
import { PlantProvider } from '@/contexts/PlantContext';
import { RecipeProvider } from '@/contexts/RecipeContext';

// ==============================|| THEME CONTEXT ||============================== //
export const ThemeContext = createContext({
  isDark: false,
  toggleTheme: () => {},
  fontScale: 'md',
  setFontScale: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ==============================|| APP - THEME, ROUTER, LOCAL  ||============================== //
const loading = (
  <div className="flex items-center justify-center min-h-screen">
    <Spin size="large" tip="Loading..." />
  </div>
);

const App = () => {
  // Font scale: sm | md | lg | xl
  const [fontScale, setFontScale] = useState(() => {
    return localStorage.getItem('fontScale') || 'md';
  });

  useEffect(() => {
    localStorage.setItem('fontScale', fontScale);
    document.documentElement.setAttribute('data-font-scale', fontScale);
  }, [fontScale]);

  // Initialize dark mode from localStorage or system preference
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Default to light mode when the user hasn't chosen a theme yet (ignore the
    // OS dark preference so the app opens in light mode on first login).
    return false;
  });

  // Update document class and localStorage when theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  // Get theme configuration based on current mode and font scale
  const themeConfig = getAntdTheme(isDark, fontScale);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, fontScale, setFontScale }}>
      <ConfigProvider theme={themeConfig}>
        <HashRouter>
          <Suspense fallback={loading}>
            <PlantProvider>
              <RecipeProvider>
                <ScrollTop>
                  <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                    <Routes />
                    <ChatProvider>
                      <div className="App">
                        <FloatingChat />
                      </div>
                    </ChatProvider>
                  </div>
                </ScrollTop>
              </RecipeProvider>
            </PlantProvider>
          </Suspense>
        </HashRouter>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default App;
